/**
 * useAssumptions Hook
 * Derives forecast assumptions from forecast account driver configurations
 *
 * Note: No backend API exists for assumptions - they are derived from forecast accounts
 */

import { useMemo } from 'react';
import type { Assumption, AssumptionScope, DriverType } from '../types';
import { useForecastData } from './useForecastData';

// ============ Query Keys ============

export const assumptionQueryKeys = {
  all: ['forecast-v3', 'assumptions'] as const,
  list: (orgId: number, locationId: number) =>
    [...assumptionQueryKeys.all, 'list', { orgId, locationId }] as const,
  byScope: (orgId: number, locationId: number, scope: AssumptionScope) =>
    [...assumptionQueryKeys.list(orgId, locationId), scope] as const,
};

// ============ Hook Parameters ============

interface UseAssumptionsParams {
  orgId: number;
  locationId: number;
  scope?: AssumptionScope | 'all';
  enabled?: boolean;
}

// ============ Return Types ============

interface UseAssumptionsReturn {
  assumptions: Assumption[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// ============ Helper Functions ============

/**
 * Get a human-readable name for a driver type assumption
 */
function getAssumptionName(driverType: DriverType, driverInput: number | null): string {
  switch (driverType) {
    case '$ / ORN':
    case '$ / ORN LM':
    case '$ / ORN LY':
      return `$POR: $${driverInput?.toFixed(2) || '0.00'}`;
    case '$ / ARN':
    case '$ / ARN LM':
    case '$ / ARN LY':
      return `$PAR: $${driverInput?.toFixed(2) || '0.00'}`;
    case '% of Revenue':
    case '% of Selected Revenue':
      return `Revenue %: ${driverInput || 0}%`;
    case 'YoY Growth %':
      return `YoY Growth: ${driverInput || 0}%`;
    case 'REVPAR Projection':
      return `Target REVPAR: $${driverInput?.toFixed(2) || '0.00'}`;
    case 'Occupancy Forecast':
      return `Target Occupancy: ${driverInput || 0}%`;
    case 'Static Entry':
      return `Fixed Amount: $${driverInput?.toFixed(0) || '0'}`;
    case 'LY Same Month':
      return 'Last Year Same Month';
    default:
      return driverType;
  }
}

/**
 * Get assumption type based on driver type
 */
function getAssumptionType(driverType: DriverType): 'Scalar' | 'Series' {
  // Series = time-based data from external source
  const seriesDrivers: DriverType[] = [
    'OTB Forecast',
    'RMS Forecast',
    'Budget',
    '$ LM',
    'LY Same Month',
  ];
  return seriesDrivers.includes(driverType) ? 'Series' : 'Scalar';
}

/**
 * Get data source for driver type
 */
function getDataSource(driverType: DriverType): 'Manual' | 'PMS' | 'RMS' | 'Budget' | 'Accounting' {
  if (driverType === 'OTB Forecast') return 'PMS';
  if (driverType === 'RMS Forecast') return 'RMS';
  if (driverType === 'Budget') return 'Budget';
  if (driverType.includes('LY') || driverType.includes('LM')) return 'Accounting';
  return 'Manual';
}

/**
 * Create a unique key for an assumption (driver type + input combination)
 */
function createAssumptionKey(driverType: DriverType, driverInput: number | null): string {
  return `${driverType}|${driverInput ?? 'null'}`;
}

// ============ Main Hook ============

export function useAssumptions({
  orgId,
  locationId,
  scope = 'all',
  enabled = true,
}: UseAssumptionsParams): UseAssumptionsReturn {
  // Get forecast data which includes accounts with driver configurations
  const {
    accounts,
    isLoading,
    error,
    refetch,
  } = useForecastData({
    orgId,
    locationId,
    year: new Date().getFullYear(),
    enabled,
  });

  // Derive assumptions from forecast accounts
  const assumptions = useMemo((): Assumption[] => {
    if (!accounts || accounts.length === 0) return [];

    // Group accounts by driver type + input combination
    const assumptionMap = new Map<string, {
      driverType: DriverType;
      driverInput: number | null;
      accountCount: number;
      departments: Set<string>;
    }>();

    accounts.forEach((account) => {
      const key = createAssumptionKey(account.driverType, account.driverInput);

      if (!assumptionMap.has(key)) {
        assumptionMap.set(key, {
          driverType: account.driverType,
          driverInput: account.driverInput,
          accountCount: 0,
          departments: new Set(),
        });
      }

      const entry = assumptionMap.get(key)!;
      entry.accountCount++;
      entry.departments.add(account.department);
    });

    // Convert to Assumption array
    const derivedAssumptions: Assumption[] = [];
    let id = 1;

    assumptionMap.forEach((entry, key) => {
      // Skip Manual/empty drivers
      if (entry.driverType === 'Manual' && entry.driverInput === null) {
        return;
      }

      // Determine scope based on department count
      let assumptionScope: AssumptionScope = 'Global';
      if (entry.departments.size === 1) {
        assumptionScope = 'Department';
      } else if (entry.departments.size < 3) {
        assumptionScope = 'Property';
      }

      // Format value for display
      let displayValue: string;
      if (entry.driverInput !== null) {
        if (entry.driverType.includes('%') || entry.driverType === 'YoY Growth %' || entry.driverType === 'Occupancy Forecast') {
          displayValue = `${entry.driverInput}%`;
        } else if (entry.driverType.includes('$') || entry.driverType === 'REVPAR Projection' || entry.driverType === 'Static Entry') {
          displayValue = `$${entry.driverInput.toFixed(2)}`;
        } else {
          displayValue = entry.driverInput.toString();
        }
      } else {
        displayValue = 'Auto-calculated';
      }

      derivedAssumptions.push({
        id: id++,
        name: getAssumptionName(entry.driverType, entry.driverInput),
        type: getAssumptionType(entry.driverType),
        scope: assumptionScope,
        value: displayValue,
        numericValue: entry.driverInput ?? undefined,
        usedBy: entry.accountCount,
        lastUpdated: new Date().toISOString().split('T')[0],
        source: getDataSource(entry.driverType),
      });
    });

    // Sort by usage count (most used first)
    derivedAssumptions.sort((a, b) => b.usedBy - a.usedBy);

    return derivedAssumptions;
  }, [accounts]);

  // Filter by scope if specified
  const filteredAssumptions = useMemo(() => {
    if (scope === 'all') return assumptions;
    return assumptions.filter((a) => a.scope === scope);
  }, [assumptions, scope]);

  return {
    assumptions: filteredAssumptions,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

export default useAssumptions;
